import { NextPage } from 'next';
import { useContext, useEffect } from 'react';
import getConfig from 'next/config';
import { NextSeo, ProductJsonLd, ProductJsonLdProps } from 'next-seo';
import {
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-core';
import {
  CollectionProducts,
  CollectionHub,
  ICollectionHubPrismicContent,
} from '@screens/Collection';
import {
  ICollection,
  ICollectionProductBasket,
  ISearchState,
  IUserData,
  TCollectionType,
} from '@monofront/types';
import {
  getFormattedCollectionProductBasket,
  getUserData,
} from '@monofront/business/utils';
import CustomError from '@screens/CustomError';
import BreadcrumbList from '@components/BreadcrumbList';
import { VirtualTrackSearchFilters } from '@components/VirtualTrackSearchFilters';
import {
  searchClient,
  productService,
  prismicService,
} from '@services/businessContainer';
import { injectLayoutDataTyped } from '@utils/layout';
import { fireAutoMLEvent } from '@utils/tracking';
import {
  FEATURE_FLAG_COLLECTION_HUB,
  USER_DATA_TYPE,
} from '@monofront/business/constants';
import {
  SearchParamsFilterParams,
  useSearchParamsFilter,
} from '@hooks/useSearchParamsFilter';
import { useInstantSearchNextRouting } from '@hooks/useInstantSearchNextRouting';
import {
  InstantSearchSSRProps,
  renderInstantSearchSSR,
} from '@utils/renderInstantSearchSSR';
import { injectGetServerSidePropsHeaders } from '@utils/query';
import { useFeatureIsOn } from '@monofront/business/contexts';
import { LayoutContext } from '@context/LayoutContext';
import { DeliveryBarCTA } from '@features/DeliveryBar';

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

type CollectionPageProps = InstantSearchSSRProps & {
  collection?: ICollection;
  collectionProductBasket?: ICollectionProductBasket | null;
  resultsState?: Promise<any>;
  searchStateInit?: ISearchState;
  siblingCollections?: ICollection[] | null;
  collectionHubPrismicContent?: ICollectionHubPrismicContent;
};

const filterSelectors: SearchParamsFilterParams = [
  ['page', (value) => Number(value) > 1],
];

const NextSeoSearch = ({ collection }: { collection: ICollection }) => {
  const canonicalSearchParams = useSearchParamsFilter(filterSelectors);

  return (
    <NextSeo
      title={collection.meta?.title || collection.title}
      description={
        collection.meta?.description || collection.body_html_safe?.slice(0, 200)
      }
      canonical={`${appBaseUrl}/collections/${collection.handle}${canonicalSearchParams}`}
      openGraph={{
        url: `${appBaseUrl}/collections/${collection.handle}`,
        title: collection.meta?.title || collection.title,
        description:
          collection.meta?.description ||
          collection.body_html_safe?.slice(0, 200),
        images: [
          {
            url: collection.image,
          },
        ],
      }}
    />
  );
};

const CollectionPage: NextPage<CollectionPageProps> = ({
  collection,
  collectionProductBasket,
  siblingCollections,
  baseIndexName,
  serverUrl,
  searchServerState,
  collectionHubPrismicContent,
}) => {
  const isCollectionHubAbTestEnabled = useFeatureIsOn(
    FEATURE_FLAG_COLLECTION_HUB,
  );
  const { seoCollectionsRating, seoCollectionsVolume } =
    useContext(LayoutContext);

  useEffect(() => {
    if (collection) {
      fireAutoMLEvent('category-page-view', {
        productEventDetail: {
          pageCategories: [
            {
              categories: [collection.handle],
            },
          ],
        },
      });
    }
  }, []);

  const instantSearchRouting = useInstantSearchNextRouting(
    baseIndexName,
    serverUrl,
  );

  if (!collection) {
    return <CustomError statusCode={404} />;
  }

  const jsonLD: ProductJsonLdProps = {
    productName: collection.meta?.title || collection.title,
    images: [collection.image],
    description: collection.meta?.description,
    sku: `$lafourche-${collection.handle}`,
    mpn: `$lafourche-${collection.handle}`,
    brand: 'La Fourche',
    reviews: {
      reviewRating: {
        ratingValue: 5,
        bestRating: 5,
        worstRating: 1,
      },
      author: 'Léo',
    },
    aggregateRating: {
      ratingValue: seoCollectionsRating,
      reviewCount: seoCollectionsVolume,
    },
  };

  return (
    <InstantSearchSSRProvider {...searchServerState}>
      <InstantSearch
        indexName={baseIndexName}
        searchClient={searchClient}
        routing={instantSearchRouting}
        insights
        future={{ preserveSharedStateOnUnmount: true }}
      >
        {searchServerState && <NextSeoSearch collection={collection} />}
        {searchServerState && <BreadcrumbList name={collection.title} />}
        {searchServerState && <ProductJsonLd {...jsonLD} />}
        {searchServerState && (
          <div className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-px-3 desktop:tw-mx-auto desktop:tw-min-w-[500px] desktop:tw-max-w-[500px] desktop:tw-p-0">
            <DeliveryBarCTA />
          </div>
        )}

        <VirtualTrackSearchFilters />
        {collection?.type === TCollectionType.hub &&
        isCollectionHubAbTestEnabled ? (
          <CollectionHub
            collection={collection}
            siblingCollections={siblingCollections}
            collectionHubPrismicContent={collectionHubPrismicContent}
          />
        ) : (
          <CollectionProducts
            collection={collection}
            collectionProductBasket={collectionProductBasket}
            siblingCollections={siblingCollections}
          />
        )}
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

export const getServerSideProps =
  injectGetServerSidePropsHeaders<CollectionPageProps>(
    injectLayoutDataTyped<CollectionPageProps>(
      async ({ req, res, query, locale, previewData }) => {
        if (!locale) {
          return {
            notFound: true,
          };
        }

        const collection = await productService.getCollection({
          query: { handle: query.collectionHandle },
          fetchChildren: true,
          locale,
        });

        if (!collection) {
          return { notFound: true };
        }

        const { baseIndexName, serverUrl, searchServerState } =
          await renderInstantSearchSSR({ req, locale }, CollectionPage, {
            collection,
          });

        const indexResult =
          searchServerState.initialResults[baseIndexName]?.results?.[0];

        if (indexResult && indexResult.nbHits === 0) {
          res.statusCode = 404;
        }

        if (
          indexResult?.page &&
          indexResult?.nbPages &&
          indexResult.page > indexResult.nbPages
        ) {
          return {
            redirect: {
              destination: `/collections/${query.collectionHandle}`,
              permanent: false,
            },
          };
        }

        const siblingCollections = collection?.parent
          ? await productService.getCollections({
              query: {
                parent: collection?.parent,
                exclude: [`handle:"${query.collectionHandle}"`],
              },
              locale,
            })
          : null;

        // Algolia API type it as an object, instantsearch type it as an array
        // and guess what do we get? Yes, an array.
        // No info on the web for this mismatch that seems to exists since many versions.
        // Also note that userData seems to refer to another concept in the doc of Algolia Search,
        // but we still get the **QueryRules** array here.
        const userData = indexResult?.userData as Array<IUserData> | undefined;

        const userDataProductBasketCarousel = getUserData(
          userData,
          USER_DATA_TYPE.PRODUCT_BASKET_CAROUSEL,
        );

        const collectionProductBasket =
          userDataProductBasketCarousel?.tag &&
          userDataProductBasketCarousel?.title
            ? await prismicService.getCollectionProductBasket({
                tag: userDataProductBasketCarousel.tag as string,
                previewData,
                ...(locale && { options: { lang: `${locale}-${locale}` } }),
              })
            : null;

        const formattedCollectionProductBasket =
          userDataProductBasketCarousel?.title
            ? getFormattedCollectionProductBasket(
                collectionProductBasket,
                userDataProductBasketCarousel?.title,
                userDataProductBasketCarousel?.descriptionMobile,
              )
            : null;

        let collectionHubPrismicContent;

        if (collection?.handle && collection.type === TCollectionType.hub) {
          try {
            const response = await prismicService.getDocumentByUid(
              'collectionhub',
              collection.handle,
              previewData,
              locale ? { lang: `${locale}-${locale}` } : {},
            );

            if (response) {
              collectionHubPrismicContent = response.data;
            }
          } catch (err) {
            if (err) {
              collectionHubPrismicContent = null;
            }
          }
        }

        return {
          props: {
            collection,
            baseIndexName,
            serverUrl,
            searchServerState,
            collectionProductBasket: formattedCollectionProductBasket,
            siblingCollections,
            collectionHubPrismicContent,
          },
        };
      },
    ),
  );

export default CollectionPage;
